import { TFunction, useTranslation } from 'react-i18next';
import {
  Alert,
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  Grid,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  PillVariant,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  TitleHeader,
  Tooltip,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import {
  CoordinatorAccessArrangementListing,
  PostCoordinatorResponseData,
  usePostCoordinatorCandidateListing,
} from '../../../api-refactored/coordinator-access-arrangement/usePostCoordinatorCandidateListing';
import { ApplicationStatus } from '../../../modules/accessArrangment/components/candidate/AccessArrangements/AccessArrangementsModels';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import RouteKey from '../../../routes/key';
import { useDeleteAccessArrangementRequest } from '../../../api/candidate/access-arrangement/@candidate/queries-services/queries';

const { accessArrangement } = RouteKey.child;
const {
  coordinatorRoot,
  coordinatorImportantNotes,
  coordinatorSelectCondition,
  coordinatorSummary,
} = RouteKey['access-arrangement'].child;

const useStyles = makeStyles()((theme) =>
({
  buttonContainer: {
    display: 'inline-flex',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
  iconButtonContainer: {
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  iconRemove: {
    margin: 'auto',
  },

  button: {
    margin: 'auto',
    padding: 0,
    width: 'max-content',
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  paddingRight6: {
    paddingRight: theme.spacing(6),
  },
  buttonRemove: {
    paddingLeft: '10px !important',
    color: theme.color.state.stateError,
  },
  statement: {
    marginBottom: theme.spacing(4),
  },
  searchContainer: {
    paddingBottom: theme.spacing(3),
  },
  navigationButtonContainer: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
  textDisabledForDraft: {
    color: theme.color.text.textDisabled,
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  lastStatement: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(7),
  },
}));

const accessArrangementAlert = (
  data: PostCoordinatorResponseData,
  classes: Record<string,string>,
  t: TFunction<'translation'>
  // eslint-disable-next-line consistent-return
) => {
  if (data.noExamRegistered) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('accessArrangement->>AccessArrangements.NoRegisteredExam')}
      />
    );
  }
  const unavailableExamLevels = getUnavailableExamLevels(data.accessArrangementPage.content);

  if (unavailableExamLevels.length) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('accessArrangement->>AccessArrangements.Unavailable', {
          examLevels: unavailableExamLevels,
        })}
      />
    );
  }
  return <></>;
};

const getUnavailableExamLevels = (
  accessArrangementListing: CoordinatorAccessArrangementListing[]
) => {
  const unavailableExamLevels: string[] = [];

  accessArrangementListing.forEach((accessArrangementItem) => {
    if (
      accessArrangementItem.requestStatus === ApplicationStatus.DRAFT &&
      !accessArrangementItem.accessArrangementOpen
    ) {
      accessArrangementItem.examLevels.split(',').forEach((examLevel) => {
        if (
          unavailableExamLevels.every((unavailableExamLevel) => unavailableExamLevel !== examLevel)
        ) {
          unavailableExamLevels.push(examLevel);
        }
      });
    }
  });
  unavailableExamLevels.sort((a, b) => a.localeCompare(b));

  return unavailableExamLevels.join(', ');
};

const getTableColumn = (
  t: any,
  classes: any,
  state: TableState,
  theme: AugmentedTheme,
  history: any,
  handleOpenDeleteModal: any
): MUIDataTableColumnDef[] => [
    {
      name: 'nricFin',
      label: t('accessArrangement->>CoordinatorSelectCandidate.NRIC/FIN/PassportNo'),
      options: {
        setCellProps: () => ({ style: { width: '14%', paddingTop: '35px' } }),
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{state.data[dataIndex].nricFin}</Typography>
        ),
      },
    },
    {
      name: 'candidateName',
      label: t('accessArrangement->>CoordinatorSelectCandidate.CandidateName'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '17%', paddingTop: '35px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{state.data[dataIndex].candidateName}</Typography>
        ),
      },
    },
    {
      name: 'examLevels',
      label: t('accessArrangement->>CoordinatorSelectCandidate.ExamLevel'),
      options: {
        setCellProps: () => ({ style: { width: '23%', paddingTop: '35px' } }),
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography noWrap variant="body2">
            {state.data[dataIndex].examLevels}
          </Typography>
        ),
      },
    },
    {
      name: 'applicationDateTime',
      label: 'Application Date/Time',
      options: {
        setCellProps: () => ({ style: { width: '13%', paddingTop: '35px' } }),
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined || state.data === undefined) {
            return [];
          }

          const accessArrangementResultModel = state.data[dataIndex];

          return stringToLongDateAndShortTime(accessArrangementResultModel.applicationDateTime);
        },
      },
    },
    {
      name: 'aaOutcomeLastUpdateDate',
      label: 'Application Outcome',
      options: {
        setCellProps: () => ({ style: { width: '25%', paddingTop: '35px' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (dataIndex === undefined || state.data === undefined) {
            return '-';
          }

          const accessArrangementResultModel = state.data[dataIndex];

          if (!accessArrangementResultModel.aaOutcomeLastUpdateDate) {
            return '-';
          }
          return `${t('accessArrangement->>AccessArrangements.LastUpdatedOn', {
            date: stringToSemanticDisplay(
              accessArrangementResultModel.aaOutcomeLastUpdateDate?.toString()
            ),
          })}`;
        },
      },
    },
    {
      name: 'requestStatus',
      label: 'Status',
      options: {
        setCellProps: () => ({ style: { width: '13%', paddingTop: '35px' } }),
        sort: false,
        customBodyRender: (value: any) => {
          let variant: PillVariant;

          switch (value) {
            case ApplicationStatus.COMPLETED:
              variant = 'positive';
              break;

            case ApplicationStatus.SUBMITTED:
            case ApplicationStatus.PROCESSING:
            case ApplicationStatus.DRAFT:
              variant = 'info';
              break;

            case ApplicationStatus.WITHDRAWN:
              variant = 'default';
              break;

            default:
              return null;
          }

          return <Pill variant={variant} label={value} />;
        },
      },
    },
    {
      name: 'action',
      label: 'Action(s)',
      options: {
        setCellProps: () => ({ style: { width: '20%' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.buttonContainer}>
            {state.data[dataIndex].requestStatus === ApplicationStatus.SUBMITTED && (
              <>
                <Button
                  id={`button-accessArrangement-view-${dataIndex}`}
                  className={`${classes.button} ${classes.buttonFont}`}
                  onClick={() =>
                    history.push(
                      `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorSummary.route}/${state.data[dataIndex].uuid}`,
                      { pageSize: state.size, page: state.page }
                    )
                  }
                  variant="text"
                  color="primary"
                  size="small"
                >
                  {t('accessArrangement->>AccessArrangements.View')}
                </Button>
                {state.data[dataIndex].amendmentDraft && (
                  <Tooltip
                    arrow
                    placement="bottom"
                    title={<>{t('accessArrangement->>AccessArrangements.AmendmentDraft')}</>}
                  >
                    <IconButton size="large">
                      <Icon name={IconName.alertOff} color={theme.color.icon.iconDefault} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {state.data[dataIndex].requestStatus === ApplicationStatus.DRAFT && (
              <Button
                id={`button-accessArrangement-continue-${dataIndex}`}
                className={`${classes.button} ${classes.paddingRight6} ${classes.buttonFont}`}
                onClick={() => {
                  const { candidateName } = state.data[dataIndex];
                  localStorage.setItem(
                    `candidateNameContinueApplication: ${state.data[dataIndex].uuid}`,
                    candidateName
                  );
                  history.push(
                    `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorSelectCondition.route}/${state.data[dataIndex].uuid}`
                  );
                }}
                variant="text"
                color="primary"
                size="small"
                disabled={!state.data[dataIndex].accessArrangementOpen}
              >
                {t('accessArrangement->>AccessArrangements.Continue')}
              </Button>
            )}
            {state.data[dataIndex].requestStatus === ApplicationStatus.DRAFT && (
              <Box className={`${classes.buttonContainer} ${classes.iconButtonContainer}`}>
                <Icon
                  size="M"
                  className={classes.iconRemove}
                  name={IconName.trash}
                  color={theme.color.icon.iconTrash}
                />
                <Button
                  id="button-accessArrangement-delete"
                  className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                  onClick={() => handleOpenDeleteModal(state.data[dataIndex].uuid)}
                  variant="text"
                  size="small"
                  style={{ padding: 0, minWidth: 0 }}
                >
                  {t('accessArrangement->>AccessArrangements.Delete')}
                </Button>
              </Box>
            )}
          </Box>
        ),
      },
    },
  ];
const AccessArrangementsListing = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { t } = useTranslation();
  const history = useHistory<any>();
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteAccessArrangementRequest.mutate(deleteData);
    }

    handleCloseDeleteModal();
  };

  const {
    data: listingData,
    mutate: mutatePostCoordinatorCandidateListing,
    isLoading,
  } = usePostCoordinatorCandidateListing({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.accessArrangementPage.content,
            count: data.accessArrangementPage.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: history?.location?.state?.page || 0,
    size: history?.location?.state?.pageSize || 5,
    sortColumn: 'applicationDateTime',
    order: 'desc',
  });

  const refreshAACandidateListing = () => {
    mutatePostCoordinatorCandidateListing({
      search: state.search || '',
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
        order: state.order,
      },
    });
  };

  useEffect(refreshAACandidateListing, [
    state.page,
    state.search,
    state.size,
    state.order,
    mutatePostCoordinatorCandidateListing,
  ]);

  const deleteAccessArrangementRequest = useDeleteAccessArrangementRequest(
    refreshAACandidateListing
  );

  const tableColumns = getTableColumn(t, classes, state, theme, history, handleOpenDeleteModal);

  const pushImportantNotes = () => {
    history.push(
      `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorImportantNotes.route}`
    );
  };

  return (
    <>
      {listingData && (
        <>
          <TitleHeader title={t('accessArrangement->>AccessArrangements.TitleHeader')} />
          <ContentTemplate type="full" transparent>
            <Box className={classes.headerContainer}>
              <SectionHeader title={t('accessArrangement->>AccessArrangements.SectionHeader')} />
              <Button
                fullWidth={isMobile}
                onClick={pushImportantNotes}
                disabled={!listingData.accessArrangementWindowOpened}
                id="button-new-application"
              >
                {t('accessArrangement->>AccessArrangements.NewApplication')}
              </Button>
            </Box>
            {accessArrangementAlert(listingData, classes, t)}
            <Grid xs={12}>
              <Box>
                <SearchBar
                  name="searchInput"
                  ariaLabel="searchInput"
                  defaultValue={searchValue}
                  placeholder={t(
                    'accessArrangement->>CoordinatorSelectCandidate.SearchPlaceHolder'
                  )}
                  onSearch={(str) => {
                    setSearchValue(str);
                    tableDispatch({
                      type: 'UPDATE_SEARCH',
                      payload: str.trim(),
                    });
                  }}
                  className={classes.searchContainer}
                  searchButton={!isMobile}
                  clearInputButton
                />
              </Box>
            </Grid>

            <MUIDataTable
              // @ts-ignore
              isLoading={isLoading}
              state={state}
              dispatch={tableDispatch}
              columns={tableColumns}
              // @ts-ignore
              data={state.data}
              emptyDataLabel={t('accessArrangement->>AccessArrangements.Empty')}
              emptyDataLabelAdditional={
                <Button
                  variant="text"
                  color="primary"
                  onClick={pushImportantNotes}
                  disabled={!listingData.accessArrangementWindowOpened}
                >
                  {t('accessArrangement->>AccessArrangements.Apply')}
                </Button>
              }
              options={{
                ...tOptions,
                filter: false,
                sort: true,
                pagination: true,
                sortOrder: {
                  name: state.sortColumn || 'applicationDateTime',
                  direction: state.order || 'desc',
                },
              }}
            />

            <Typography className={classes.lastStatement}>
              {t('accessArrangement->>AccessArrangements.AAListingStatement')}
            </Typography>
            <CustomDialog
              open={openDeleteModal}
              title={t('accessArrangement->>AccessArrangements.Delete Title')}
              message={t('accessArrangement->>AccessArrangements.Delete Message')}
              action={
                <APIActions
                  onClose={handleCloseDeleteModal}
                  apiCall={handleDelete}
                  cancelString={t('accessArrangement->>AccessArrangements.Cancel')}
                  apiString={t('accessArrangement->>AccessArrangements.Proceed')}
                />
              }
            />
          </ContentTemplate>
        </>
      )}
    </>
  );
};

export default AccessArrangementsListing;
